import { createContext, useContext, useCallback, useMemo, useState, useEffect } from 'react';
import { t as translate } from '../translations.js';

const LocaleContext = createContext(null);

export function useLocale() {
  const context = useContext(LocaleContext);
  if (!context) {
    throw new Error('useLocale must be used within a LocaleProvider');
  }
  return context;
}

export function LocaleProvider({ children }) {
  const [locale, setLocaleState] = useState(() => localStorage.getItem('locale') || 'en-US');
  const [localeCountry, setLocaleCountryState] = useState(() => localStorage.getItem('localeCountry') || 'United States');

  useEffect(() => {
    const handleLocaleChanged = (e) => {
      if (e.detail?.locale) {
        setLocaleState(e.detail.locale);
        if (e.detail.country) setLocaleCountryState(e.detail.country);
      }
    };
    window.addEventListener('localeChanged', handleLocaleChanged);
    return () => window.removeEventListener('localeChanged', handleLocaleChanged);
  }, []);

  const setLocale = useCallback((newLocale, countryName) => {
    if (newLocale) {
      localStorage.setItem('locale', newLocale);
      setLocaleState(newLocale);
    }
    if (countryName) {
      localStorage.setItem('localeCountry', countryName);
      setLocaleCountryState(countryName);
    }
  }, []);

  const t = useCallback(
    (key, opts = {}) => translate(locale, key, opts),
    [locale]
  );

  const value = useMemo(
    () => ({ locale, localeCountry, setLocale, t }),
    [locale, localeCountry, setLocale, t]
  );

  return (
    <LocaleContext.Provider value={value}>
      {children}
    </LocaleContext.Provider>
  );
}
